import React, { useRef, useState } from "react";
import { useTranslation } from "../../hooks/useTranslation";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, DeleteSweep, KeyboardReturn } from "@mui/icons-material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useWorkspaceStore } from "../../store/useWorkspaceStore";
import { WorkspaceItemCard } from "./WorkspaceItemCard";
import { WorkspaceNoteCard } from "./WorkspaceNoteCard";

const DRAWER_WIDTH = 390;

export const WorkspaceDrawer = () => {
  const { isOpen, close, items, addNote, reorder, clearAll } =
    useWorkspaceStore();
  const { translate: t } = useTranslation("workspace");
  const [noteInput, setNoteInput] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleNoteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const trimmed = noteInput.trim();
      if (trimmed) {
        addNote(trimmed);
        setNoteInput("");
      }
    }
  };

  const handleAddClick = () => {
    const trimmed = noteInput.trim();
    if (trimmed) {
      addNote(trimmed);
      setNoteInput("");
      inputRef.current?.focus();
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    reorder(reordered);
  };

  const handleClearAll = () => {
    clearAll();
    setConfirmOpen(false);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={close}
        PaperProps={{
          sx: { width: DRAWER_WIDTH, display: "flex", flexDirection: "column" },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          py={1.5}
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {t("title")}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            {items.length > 0 && (
              <Tooltip title={t("clearAll.tooltip")}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setConfirmOpen(true)}
                >
                  <DeleteSweep fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={close}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Note input */}
        <Box
          px={2}
          py={1.5}
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <TextField
            inputRef={inputRef}
            multiline
            maxRows={4}
            fullWidth
            size="small"
            placeholder={t("noteInput.placeholder")}
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            onKeyDown={handleNoteKeyDown}
            slotProps={{
              input: {
                endAdornment: noteInput.trim() && (
                  <InputAdornment position="end">
                    <Tooltip title={t("noteInput.saveTooltip")}>
                      <IconButton size="small" onClick={handleAddClick}>
                        <KeyboardReturn fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
            sx={{ "& .MuiInputBase-root": { fontSize: "0.85rem" } }}
          />
        </Box>

        {/* Items list with DnD */}
        <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2 }}>
          {items.length === 0 ? (
            <Typography
              variant="body2"
              color="text.disabled"
              textAlign="center"
              py={6}
            >
              {t("empty")}
            </Typography>
          ) : (
            <>
              <Box display="flex" alignItems="center" mb={1.5}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                >
                  {t("itemsLabel", { count: items.length })}
                </Typography>
              </Box>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="workspace-list">
                  {(provided) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                      {items.map((item, index) => (
                        <Draggable
                          key={item.uid}
                          draggableId={item.uid}
                          index={index}
                        >
                          {(drag) => (
                            <Box ref={drag.innerRef} {...drag.draggableProps}>
                              {item.type === "note" ? (
                                <WorkspaceNoteCard
                                  item={item}
                                  dragHandleProps={drag.dragHandleProps}
                                />
                              ) : (
                                <WorkspaceItemCard
                                  item={item}
                                  dragHandleProps={drag.dragHandleProps}
                                />
                              )}
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Box>
      </Drawer>

      {/* Confirm clear all */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("clearAll.dialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("clearAll.description")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {t("clearAll.cancel")}
          </Button>
          <Button onClick={handleClearAll} color="error" variant="contained">
            {t("clearAll.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
